import { useMemo } from 'react';
import { keepPreviousData } from '@tanstack/react-query';

import { Filter } from '@playq/octopus-common';
import { CredentialsSort, QueryCredentialsResponse } from '@playq/octopus2-auth';
import { GenericFailure, OffsetLimit } from '@playq/services-shared';

import { IUseQueryOptionsExtended, useEitherQuery, useQueryPrefetch } from '/api/service-hooks';
import { services2 } from '/api/services2';

const emptyArray: [] = [];
const emptyFilterBy: { [key: string]: Filter } = {};

export const queryCredentialsKeys: unknown[] = ['service', 'public', 'query', 'credentials'];

export const useQueryCredentials = (
  iterator: OffsetLimit,
  sortBy: CredentialsSort[] = emptyArray,
  filterBy: { [key: string]: Filter } = emptyFilterBy,
  options?: IUseQueryOptionsExtended<QueryCredentialsResponse | undefined, GenericFailure | Error>
) => {
  const keys = useMemo(() => queryCredentialsKeys.concat(iterator, sortBy, filterBy), [iterator, sortBy, filterBy]);

  const res = useEitherQuery(keys, () => services2.credentialsService.queryCredentials(iterator, sortBy, filterBy), {
    placeholderData: keepPreviousData,
    ...options,
  });

  const total = useMemo(() => res.data?.total ?? 0, [res.data?.total]);

  useQueryPrefetch({
    keys,
    enabled: options?.enablePrefetch,
    total,
    args: [iterator, sortBy, filterBy],
    serviceMethod: services2.credentialsService.queryCredentials.bind(services2.credentialsService),
  });

  return { ...res, credentials: res.data?.entities || emptyArray, total, tags: keys };
};
